import { useCallback, useMemo } from 'react'
import { Graph } from 'Common/Graph/Graph'
import { StockProjectionExceptionsProps } from './StockProjection.types'
import { format } from 'date-fns'

const hovertemplate = () => {
  return `%{text}<br>` + `<extra></extra>`
}

export const StockProjectionExceptionsGraph = ({
  xAxisData,
  stockProjectionExceptionData,
  stockProjectionAlertData,
  stockProjectionOkData,
}: StockProjectionExceptionsProps) => {
  const colors = {
    stockProjectionExceptionColor: '#E87722',
    stockProjectionAlertColor: '#F3E500',
    stockProjectionOkColor: '#6CC24A',
  }

  const weeksFormatted = useMemo(() => {
    return xAxisData?.map((dateText: string, index: number) => {
      return `${format(new Date(dateText), 'dd MMM yyy')} (Week ${index + 1})`
    })
  }, [xAxisData])

  const renderHoverText = useCallback(
    (label: string, data: number[]) => {
      return data.map(
        (d: number, i: number) =>
          `   <b>${label}</b><br>` +
          `   <b>Week:  </b>${weeksFormatted[i]}<br>` +
          `   <b>Number of SKUs: </b>${d.toFixed(1)}<br> `
      )
    },
    [xAxisData]
  )

  const trace1 = {
    x: xAxisData,
    y: stockProjectionExceptionData,
    text: renderHoverText('Projected stock-out', stockProjectionExceptionData),
    hovertemplate: hovertemplate(),
    textfont: { color: 'transparent' },
    marker: {
      color: colors.stockProjectionExceptionColor,
    },
    name: 'Projected stock-out',
    type: 'bar',
  }

  const trace2 = {
    x: xAxisData,
    y: stockProjectionAlertData,
    text: renderHoverText('Projected low stock', stockProjectionAlertData),
    hovertemplate: hovertemplate(),
    textfont: { color: 'transparent' },
    marker: {
      color: colors.stockProjectionAlertColor,
    },
    name: 'Projected low stock',
    type: 'bar',
  }

  const trace3 = {
    x: xAxisData,
    y: stockProjectionOkData,
    text: renderHoverText('Stock projection on target', stockProjectionOkData),
    hovertemplate: hovertemplate(),
    textfont: { color: 'transparent' },
    marker: {
      color: colors.stockProjectionOkColor,
    },
    name: 'Stock projection on target',
    type: 'bar',
  }

  const data = useMemo(
    () => [trace1, trace2, trace3],
    [
      xAxisData,
      stockProjectionExceptionData,
      stockProjectionAlertData,
      stockProjectionOkData,
    ]
  )

  const layout = {
    barmode: 'stack',
    height: 275,
    yaxis: {
      showgrid: true,
      title: {
        text: 'Number of SKUs',
      },
    },
    xaxis: {
      ntick: 10,
      tickformat: '%d %b %y',
    },
    legend: { orientation: 'h', x: -0.05, y: -0.3 },
  }

  return <Graph data={data} layout={layout} />
}
